import React, { useEffect, useMemo } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import * as Dati from "src/Dati";
import * as DatiFide from "src/DatiFide";
import useReducerOnChange from "src/hooks/ReducerOnChange";
import ChiamaWeb, {CChiamaWebParametri} from "src/ChiamaWeb";
import { Trad } from "src/components/";
import { _AnalysisCharts } from "../../components";
import { _StateField } from "../../form";
import { getEstablishmentYears, getIndex, getPrejudicialsIndex } from "./helpers";

interface IEconomicDataProps {
    Data: DatiFide.CDatiFideValutazione;
    OnApiError: (error: Error) => void;
    OnChange: (e: React.FormEvent<any>) => void;
}

export const _EconomicData: React.FC<IEconomicDataProps> = ({ Data, OnApiError, OnChange }) => {
    const [EconomicData, setEconomicData] = useReducerOnChange<Dati.CDatiEconomici>(new Dati.CDatiEconomici());

    useEffect(() => {
        GetEconomicData();
    }, [Data.ID_DatoEconomico]);

    const GetEconomicData = (): void => {
        const Param: CChiamaWebParametri = {
            URL: `/api/DatiEconomici/seleziona?ID_DatoEconomico=${Data.ID_DatoEconomico}`,
            OnRis: OnGetEconomicData,
            OnErrore: (sender: ChiamaWeb, error: Error) => OnApiError(error)
        };
        new ChiamaWeb().Chiama(Param);
    };

    const OnGetEconomicData = (sender: ChiamaWeb, data: Dati.CRisAPIDatoDatiEconomici) => {
        setEconomicData({ Type: "Set", Value: data.Dato });
    };

    const protestsIndex = useMemo(() => getIndex(EconomicData.Protesti || ""), [EconomicData]);
    const prejudicialsIndex = useMemo(() => getPrejudicialsIndex(EconomicData.Pregiudizievoli || ""), [EconomicData]);
    const insolvencyProceedingsIndex = useMemo(() => getIndex(EconomicData.ProcedureConcorsuali || ""), [EconomicData]);
    const establishmentYears = useMemo(() => getEstablishmentYears(EconomicData.DataCostituzione), [EconomicData]);

    return (
        <Grid container spacing={5}>
            <Grid item xs={12} md={3}>
                <_StateField Id="IndiceProtesti" Label="Protesti" Value={protestsIndex} OnChange={() => {}} AcceptableLimit={2} GoodLimit={3} LowLimit={0} variant="outlined" ReverseDirection />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndicePregiudizievoli"
                    Label="Pregiudizievoli"
                    Value={prejudicialsIndex}
                    OnChange={() => {}}
                    AcceptableLimit={2}
                    GoodLimit={3}
                    LowLimit={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceProcedureConcorsuali"
                    Label="ProcedureConcorsuali"
                    Value={insolvencyProceedingsIndex}
                    OnChange={() => {}}
                    AcceptableLimit={2}
                    GoodLimit={3}
                    LowLimit={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="AnniCostituzione"
                    Label="AnniCostituzione"
                    Value={establishmentYears}
                    OnChange={() => {}}
                    AcceptableLimit={3}
                    GoodLimit={5}
                    LowLimit={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField Id="Punteggio" Label="Punteggio" Value={EconomicData.Punteggio} OnChange={() => {}} AcceptableLimit={6} GoodLimit={3} LowLimit={8} variant="outlined" />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeAssetTurnover"
                    Label="IndiceSettorialeAssetTurnover"
                    Value={EconomicData.IndiceSettorialeAssetTurnover}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeCoperturaInteressi"
                    Label="IndiceSettorialeCoperturaInteressi"
                    Value={EconomicData.IndiceSettorialeCoperturaInteressi}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeCurrentRatio"
                    Label="IndiceSettorialeCurrentRatio"
                    Value={EconomicData.IndiceSettorialeCurrentRatio}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeDurataMediaDeiDebiti"
                    Label="IndiceSettorialeDurataMediaDeiDebiti"
                    Value={EconomicData.IndiceSettorialeDurataMediaDeiDebiti}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeIndiceDiAutocoperturaDelleImmobilizzazioni"
                    Label="IndiceSettorialeIndiceDiAutocoperturaDelleImmobilizzazioni"
                    Value={EconomicData.IndiceSettorialeIndiceDiAutocoperturaDelleImmobilizzazioni}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeLeverage"
                    Label="IndiceSettorialeLeverage"
                    Value={EconomicData.IndiceSettorialeLeverage}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeOneriFinanziariValoreProduzione"
                    Label="IndiceSettorialeOneriFinanziariValoreProduzione"
                    Value={EconomicData.IndiceSettorialeOneriFinanziariValoreProduzione}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeQuickRatio"
                    Label="IndiceSettorialeQuickRatio"
                    Value={EconomicData.IndiceSettorialeQuickRatio}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeROE"
                    Label="IndiceSettorialeROE"
                    Value={EconomicData.IndiceSettorialeROE}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeROI"
                    Label="IndiceSettorialeROI"
                    Value={EconomicData.IndiceSettorialeROI}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="IndiceSettorialeROS"
                    Label="IndiceSettorialeROS"
                    Value={EconomicData.IndiceSettorialeROS}
                    OnChange={() => {}}
                    AcceptableLimit={4}
                    GoodLimit={8}
                    LowLimit={0}
                    NeutralVal={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <_StateField
                    Id="NumeroDipendenti"
                    Label="NumeroDipendenti"
                    Value={EconomicData.NumeroDipendenti}
                    OnChange={() => {}}
                    AcceptableLimit={16}
                    GoodLimit={30}
                    LowLimit={0}
                    variant="outlined"
                    ReverseDirection
                />
            </Grid>
            <Grid item xs={12}>
                <TextField multiline fullWidth variant="outlined" id="Dato.Note" label={Trad.Trad("Commenti")} rows={3} rowsMax={5} value={Data.Note} onChange={OnChange} />
            </Grid>
            <Grid item xs={12}>
                <_AnalysisCharts Data={Data} EconomicData={EconomicData} />
            </Grid>
        </Grid>
    );
};



// WEBPACK FOOTER //
// ./src/inar2/components/BondsAssessment/EconomicData.tsx